import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import API from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import ReactMarkdown from "react-markdown";

const SERVICE_NAMES = {
  "ai-quick-scan": "AI Quick Scan",
  "ai-pilot": "AI Pilot",
  "ai-workflow": "AI Workflow Automatisering",
  "ai-training": "AI Training",
  "ai-governance": "AI Governance",
};

export default function ReportView() {
  const { reportId } = useParams();
  const { t } = useLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    try {
      const { data } = await API.get(`/reports/${reportId}`);
      setReport(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${reportId}/pdf`, { credentials: "include" });
      if (!response.ok) throw new Error("PDF download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CleverTion_Report_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-6 h-6 border-2 border-[#0A3FA3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen pt-20 pb-12 bg-[#F9FAFB]">
        <div className="max-w-4xl mx-auto px-4 text-center py-20">
          <p className="text-muted-foreground">Report not found</p>
          <Link to="/dashboard"><Button variant="outline" className="mt-4 rounded-sm">Back to Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#F9FAFB]" data-testid="report-view-page">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="report-back-link">
          <ArrowLeft size={14} /> {t("intake.back")}
        </Link>

        <div className="bg-white border border-border">
          {/* Header */}
          <div className="p-8 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <img src="https://customer-assets.emergentagent.com/job_ai-governance-ready/artifacts/1y03ujcj_CleverTion.png" alt="CleverTion" className="h-6" />
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A3FA3]">AI Report</span>
                </div>
                <h1 className="text-2xl font-bold tracking-tight mt-1" data-testid="report-title">
                  {SERVICE_NAMES[report.service_id] || report.service_id}
                </h1>
              </div>
              <div className="text-right text-sm text-muted-foreground">
                <div>{new Date(report.created_at).toLocaleDateString()}</div>
                <div className="text-xs uppercase">{report.language === "nl" ? "Nederlands" : report.language === "de" ? "Deutsch" : report.language === "fr" ? "Fran\u00e7ais" : "English"}</div>
                <Button size="sm" variant="outline" className="mt-2 rounded-sm text-xs" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
                  <Download size={14} className="mr-1" /> PDF
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 report-content" data-testid="report-content">
            <ReactMarkdown>{report.content}</ReactMarkdown>
          </div>

          {/* Footer */}
          <div className="p-8 border-t border-border bg-[#F9FAFB]">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                Report ID: {report.report_id}
              </div>
              <div className="text-xs font-bold text-[#0A3FA3]">
                <img src="https://customer-assets.emergentagent.com/job_ai-governance-ready/artifacts/1y03ujcj_CleverTion.png" alt="CleverTion" className="h-5 inline" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
